import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  positionOpenerAxiosApi,
  predictionManagerAxiosApi
} from 'src/Config/axiosApi';
import type { EntryStrategy } from '../types/entry.types';

/**
 * Opener pair SUBGROUPS — a config bucket nested INSIDE one opener group.
 * Purely a config concept (subgroups never get their own pods): a subgroup
 * stores a `config` snapshot and the operator presses Apply to stamp it onto
 * every member rule via the per-rule update path. Metadata (CRUD, assignment,
 * templates, instantiate) lives on the pair-manager service alongside
 * `/pairs` (`predictionManagerAxiosApi`); Apply + drift live on the
 * position-opener because they touch `entry_rules` (`positionOpenerAxiosApi`).
 */

/**
 * The stored snapshot of opener fields a subgroup controls. Every key is
 * OPTIONAL — an absent key means "Apply does not touch this field on the
 * member rules". Mirrors `SubgroupConfig` on the backend schema.
 */
export interface SubgroupConfig {
  // Aggression / go-to-market.
  entryStrategy?: EntryStrategy;
  counterHedgeMode?:
    | 'always-threshold'
    | 'gtc-to-max-sum'
    | 'market-to-hedge';
  counterStuckMarketHedgeSeconds?: number | null;
  disablePlacementGatePolymarket?: boolean | null;
  disablePlacementGateKalshi?: boolean | null;
  counterMakerWalkStepSeconds?: number | null;
  counterMakerWalkStepCents?: number | null;
  // Maker-leg flags.
  polyYesMaker?: boolean;
  polyNoMaker?: boolean;
  kalshiYesMaker?: boolean;
  kalshiNoMaker?: boolean;
  // Sizing & limits.
  size?: number;
  maxVolumeContracts?: number;
  volumePeriod?: 'hour' | 'day';
  maxOrderSize?: number;
  makerSizeMode?: 'dynamic' | 'fixed';
  // Thresholds & timing.
  thresholdSum?: number;
  lockstepMaxAheadBatches?: number | null;
}

export interface Subgroup {
  _id: string;
  groupId: string;
  name: string;
  color?: string;
  sortOrder?: number;
  config: SubgroupConfig;
  seededFromTemplateId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateSubgroupDto {
  groupId: string;
  name: string;
  color?: string;
  sortOrder?: number;
  config?: SubgroupConfig;
}

export type UpdateSubgroupDto = Partial<
  Pick<Subgroup, 'name' | 'color' | 'sortOrder' | 'config'>
>;

/** Real GET /subgroups wire shape (mirrors the groups envelope). */
export interface SubgroupsResponse {
  subgroups: Subgroup[];
  unassignedCount: number;
}

/** A single member rule's divergence from its subgroup snapshot. */
export interface SubgroupDriftRule {
  ruleId: string;
  pairName?: string;
  /** Field keys that differ between the rule and the snapshot. */
  fields: string[];
}

/** GET /subgroups/:id/drift result. */
export interface SubgroupDriftResponse {
  matched: number;
  drifted: SubgroupDriftRule[];
}

/** POST /subgroups/:id/apply result — mirrors the group-bulk contract. */
export interface SubgroupApplyResult {
  matched: number;
  modified: number;
  skipped: number;
}

export interface SubgroupTemplateEntry {
  name: string;
  color?: string;
  sortOrder?: number;
  config: SubgroupConfig;
}

export interface SubgroupTemplateSet {
  _id: string;
  name: string;
  subgroups: SubgroupTemplateEntry[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateSubgroupTemplateDto {
  name: string;
  subgroups: SubgroupTemplateEntry[];
}

export type UpdateSubgroupTemplateDto = Partial<CreateSubgroupTemplateDto>;

/** POST /groups/:groupId/instantiate-template/:templateId result. */
export interface InstantiateTemplateResult {
  created: string[];
  skipped: string[];
}

/* ------------------------------------------------------------------ */
/* Subgroup CRUD (pair-manager)                                        */
/* ------------------------------------------------------------------ */

/**
 * Subgroups of one group. Gated by `groupId` — there is no meaningful
 * subgroup list outside a selected group, so the query is disabled until a
 * group id is passed. The full `{ subgroups, unassignedCount }` envelope is
 * returned (counts are client-computed off `.subgroups`, but the unassigned
 * total is the server's truth).
 */
export function useSubgroups(groupId: string | null | undefined) {
  return useQuery<SubgroupsResponse>(
    ['subgroups', groupId],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<
        SubgroupsResponse | Subgroup[]
      >('/subgroups', { params: { groupId } });
      // Canonical wire shape is `{ subgroups, unassignedCount }`; the
      // bare-array branch is defensive only.
      if (Array.isArray(data)) return { subgroups: data, unassignedCount: 0 };
      return {
        subgroups: data?.subgroups ?? [],
        unassignedCount: data?.unassignedCount ?? 0
      };
    },
    {
      enabled: !!groupId,
      refetchInterval: 30_000,
      keepPreviousData: true
    }
  );
}

export function useCreateSubgroup() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: CreateSubgroupDto) => {
      const { data } = await predictionManagerAxiosApi.post<Subgroup>(
        '/subgroups',
        dto
      );
      return data;
    },
    {
      mutationKey: 'entry:create-subgroup',
      onSuccess: (_data, vars) => {
        qc.invalidateQueries(['subgroups', vars.groupId]);
        qc.invalidateQueries('manual-confirmed-pairs');
      }
    }
  );
}

export function useUpdateSubgroup() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      id,
      dto
    }: {
      id: string;
      groupId: string;
      dto: UpdateSubgroupDto;
    }) => {
      const { data } = await predictionManagerAxiosApi.patch<Subgroup>(
        `/subgroups/${id}`,
        dto
      );
      return data;
    },
    {
      mutationKey: 'entry:update-subgroup',
      onSuccess: (_data, vars) => {
        qc.invalidateQueries(['subgroups', vars.groupId]);
      }
    }
  );
}

export function useDeleteSubgroup() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id }: { id: string; groupId: string }) => {
      const { data } = await predictionManagerAxiosApi.delete<{
        deleted: boolean;
        unassigned: number;
      }>(`/subgroups/${id}`);
      return data;
    },
    {
      mutationKey: 'entry:delete-subgroup',
      onSuccess: (_data, vars) => {
        qc.invalidateQueries(['subgroups', vars.groupId]);
        qc.invalidateQueries('manual-confirmed-pairs');
      }
    }
  );
}

/* ------------------------------------------------------------------ */
/* Pair assignment (pair-manager)                                      */
/* ------------------------------------------------------------------ */

export function useAssignPairSubgroup() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      pairId,
      subgroupId
    }: {
      pairId: string;
      groupId: string | null;
      subgroupId: string | null;
    }) => {
      const { data } = await predictionManagerAxiosApi.patch(
        `/pairs/${pairId}/subgroup`,
        { subgroupId }
      );
      return data;
    },
    {
      mutationKey: 'entry:assign-pair-subgroup',
      onSuccess: (_data, vars) => {
        qc.invalidateQueries('manual-confirmed-pairs');
        if (vars.groupId) qc.invalidateQueries(['subgroups', vars.groupId]);
      }
    }
  );
}

/** Backend caps `pairIds` at 100 per request (same as the group bulk). */
export const BULK_ASSIGN_SUBGROUP_CHUNK_SIZE = 100;

/**
 * Assign (or unassign with `subgroupId: null`) many pairs to a subgroup in
 * chunks of 100 — callers pass ONE id array; the chunking happens here,
 * sequentially, and the per-chunk counters are summed into one result.
 */
export async function bulkAssignPairSubgroup(
  pairIds: string[],
  subgroupId: string | null
): Promise<{ matched: number; modified: number }> {
  let matched = 0;
  let modified = 0;
  for (
    let i = 0;
    i < pairIds.length;
    i += BULK_ASSIGN_SUBGROUP_CHUNK_SIZE
  ) {
    const { data } = await predictionManagerAxiosApi.patch<{
      matched: number;
      modified: number;
    }>('/pairs/subgroup/bulk', {
      pairIds: pairIds.slice(i, i + BULK_ASSIGN_SUBGROUP_CHUNK_SIZE),
      subgroupId
    });
    matched += data.matched;
    modified += data.modified;
  }
  return { matched, modified };
}

export function useBulkAssignPairSubgroup() {
  const qc = useQueryClient();
  return useMutation(
    ({
      pairIds,
      subgroupId
    }: {
      pairIds: string[];
      groupId: string | null;
      subgroupId: string | null;
    }) => bulkAssignPairSubgroup(pairIds, subgroupId),
    {
      mutationKey: 'entry:bulk-assign-subgroup',
      onSuccess: (_data, vars) => {
        qc.invalidateQueries('manual-confirmed-pairs');
        if (vars.groupId) qc.invalidateQueries(['subgroups', vars.groupId]);
      }
    }
  );
}

/* ------------------------------------------------------------------ */
/* Apply + drift (position-opener — touches entry_rules)               */
/* ------------------------------------------------------------------ */

export type ApplyPath = 'a' | 'b' | 'both';

export function useApplySubgroup() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      id,
      path
    }: {
      id: string;
      groupId?: string | null;
      path?: ApplyPath;
    }) => {
      const { data } = await positionOpenerAxiosApi.post<SubgroupApplyResult>(
        `/rules/subgroups/${id}/apply`,
        path ? { path } : {}
      );
      return data;
    },
    {
      mutationKey: 'entry:apply-subgroup',
      onSuccess: (_data, vars) => {
        // Apply stamps the snapshot onto member rules — re-read rules + drift.
        qc.invalidateQueries(['entry-rules']);
        qc.invalidateQueries(['subgroup-drift', vars.id]);
      }
    }
  );
}

export function useSubgroupDrift(
  subgroupId: string | null | undefined,
  enabled = true
) {
  return useQuery<SubgroupDriftResponse>(
    ['subgroup-drift', subgroupId],
    async () => {
      const { data } =
        await positionOpenerAxiosApi.get<SubgroupDriftResponse>(
          `/rules/subgroups/${subgroupId}/drift`
        );
      return data;
    },
    {
      enabled: enabled && !!subgroupId,
      refetchInterval: 30_000,
      keepPreviousData: true
    }
  );
}

/* ------------------------------------------------------------------ */
/* Templates (pair-manager)                                            */
/* ------------------------------------------------------------------ */

export function useSubgroupTemplates(enabled = true) {
  return useQuery<SubgroupTemplateSet[]>(
    ['subgroup-templates'],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<
        SubgroupTemplateSet[]
      >('/subgroup-templates');
      return Array.isArray(data) ? data : [];
    },
    { enabled, keepPreviousData: true }
  );
}

export function useCreateSubgroupTemplate() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: CreateSubgroupTemplateDto) => {
      const { data } = await predictionManagerAxiosApi.post<SubgroupTemplateSet>(
        '/subgroup-templates',
        dto
      );
      return data;
    },
    {
      mutationKey: 'entry:create-subgroup-template',
      onSuccess: () => qc.invalidateQueries(['subgroup-templates'])
    }
  );
}

export function useUpdateSubgroupTemplate() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, dto }: { id: string; dto: UpdateSubgroupTemplateDto }) => {
      const { data } =
        await predictionManagerAxiosApi.patch<SubgroupTemplateSet>(
          `/subgroup-templates/${id}`,
          dto
        );
      return data;
    },
    {
      mutationKey: 'entry:update-subgroup-template',
      onSuccess: () => qc.invalidateQueries(['subgroup-templates'])
    }
  );
}

export function useDeleteSubgroupTemplate() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await predictionManagerAxiosApi.delete<{
        deleted: boolean;
      }>(`/subgroup-templates/${id}`);
      return data;
    },
    {
      mutationKey: 'entry:delete-subgroup-template',
      onSuccess: () => qc.invalidateQueries(['subgroup-templates'])
    }
  );
}

/**
 * Instantiate a template set into a group — upsert-by-name (creates missing
 * subgroups, leaves existing ones untouched; the new subgroups are EMPTY,
 * pairs are assigned by hand). Refreshes the group's subgroup list.
 */
export function useInstantiateSubgroupTemplate() {
  const qc = useQueryClient();
  return useMutation(
    async ({ groupId, templateId }: { groupId: string; templateId: string }) => {
      const { data } =
        await predictionManagerAxiosApi.post<InstantiateTemplateResult>(
          `/groups/${groupId}/instantiate-template/${templateId}`
        );
      return data;
    },
    {
      mutationKey: 'entry:instantiate-subgroup-template',
      onSuccess: (_data, vars) =>
        qc.invalidateQueries(['subgroups', vars.groupId])
    }
  );
}
